import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SEOHead from '../common/SEOHead';
import SafeIcon from '../common/SafeIcon';

const { FiTarget, FiAward, FiTrendingUp, FiUsers, FiHome, FiPhone, FiMail, FiMapPin, FiCheck } = FiIcons;

const AboutPage = () => {
  const stats = [
    { number: '500+', label: 'Properties Managed', icon: FiHome },
    { number: '98%', label: 'Client Satisfaction', icon: FiAward },
    { number: '15+', label: 'Years Experience', icon: FiTrendingUp },
    { number: '24/7', label: 'Support Available', icon: FiUsers }
  ];

  const values = [
    {
      title: 'Professionalism',
      description: 'We maintain the highest standards of professionalism in all our interactions and services.',
      icon: FiAward
    },
    {
      title: 'Transparency',
      description: 'Clear communication and honest reporting ensure you always know the status of your investment.',
      icon: FiTarget
    },
    {
      title: 'Customer Satisfaction',
      description: 'Your success is our success. We are committed to exceeding expectations in every aspect.',
      icon: FiUsers
    }
  ];

  const achievements = [
    '500+ Properties Under Management',
    '98% Client Retention Rate',
    '15+ Years in Calgary Market',
    '24/7 Emergency Support Available',
    'Licensed & Insured Professionals',
    'Calgary Business Excellence Award 2023'
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About DGM Property Management Calgary",
    "description": "Learn about DGM Property Management, Calgary's trusted property management company with 15+ years experience, 500+ properties managed, and 98% client satisfaction.",
    "url": "https://dgmpropertymanagement.ca/about",
    "mainEntity": {
      "@type": "LocalBusiness",
      "name": "DGM Property Management",
      "description": "Calgary's premier property management company offering comprehensive residential and commercial property management services.",
      "url": "https://dgmpropertymanagement.ca",
      "telephone": "587-800-1215",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Calgary",
        "addressRegion": "Alberta",
        "addressCountry": "CA"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "51.0447",
        "longitude": "-114.0719"
      },
      "openingHours": "Mo-Fr 09:00-17:00",
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": "51.0447",
          "longitude": "-114.0719"
        },
        "geoRadius": "50000"
      },
      "hasAchievement": achievements.map(achievement => ({
        "@type": "Achievement",
        "name": achievement
      }))
    }
  };

  return (
    <>
      <SEOHead
        title="About DGM Property Management Calgary | Our Story & Values"
        description="Learn about DGM Property Management, Calgary's trusted property management company with 15+ years experience, 500+ properties managed, and 98% client satisfaction. Discover our values and commitment to excellence."
        keywords="about DGM Property Management, Calgary property management company, property management story, Calgary property managers, DGM values, property management Calgary Alberta"
        canonical="/about"
        ogImage="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=630"
        schemaData={schemaData}
      />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                About DGM Property Management
              </h1>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                Calgary's trusted property management partner with 15+ years of excellence, 
                managing 500+ properties with a 98% client satisfaction rate.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Content */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="space-y-8"
              >
                <div>
                  <h2 className="text-4xl font-bold text-gray-900 mb-6">
                    Our Story
                  </h2>
                  <div className="space-y-4 text-gray-600 leading-relaxed">
                    <p>
                      DGM Property Management is Calgary's trusted partner for comprehensive property management services. 
                      We specialize in managing day-to-day operations proactively to maintain properties at optimal standards 
                      while ensuring clear communication with property owners and residents.
                    </p>
                    <p>
                      Our team focuses on maximizing your investment returns through strategic tenant placement, 
                      efficient maintenance coordination, and transparent financial management. We serve the Calgary area 
                      with additional services in Airdrie, providing localized expertise and personalized attention.
                    </p>
                    <p>
                      With our commitment to professionalism, transparency, and customer satisfaction, we handle everything 
                      from tenant acquisition to property enhancement, allowing you to enjoy the benefits of real estate 
                      investment without the day-to-day hassles.
                    </p>
                  </div>
                </div>

                {/* Values */}
                <div className="space-y-6">
                  <h3 className="text-2xl font-semibold text-gray-900">Our Core Values</h3>
                  <div className="space-y-4">
                    {values.map((value, index) => (
                      <motion.div
                        key={value.title}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: index * 0.1 }}
                        className="flex gap-4"
                      >
                        <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                          <SafeIcon icon={value.icon} className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-1">{value.title}</h4>
                          <p className="text-gray-600 text-sm">{value.description}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Image and Stats */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="space-y-8"
              >
                {/* Image */}
                <div className="relative overflow-hidden rounded-2xl shadow-xl">
                  <img
                    src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600"
                    alt="Calgary skyline and modern buildings - DGM Property Management service area"
                    className="w-full h-[400px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 to-transparent"></div>
                  <div className="absolute bottom-6 left-6 text-white">
                    <h4 className="text-xl font-semibold">Calgary & Airdrie</h4>
                    <p className="text-gray-200">Serving Alberta's Premier Markets</p>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-6">
                  {stats.map((stat, index) => (
                    <motion.div
                      key={stat.label}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="bg-white rounded-xl p-6 text-center shadow-lg"
                    >
                      <div className="flex justify-center mb-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                          <SafeIcon icon={stat.icon} className="w-6 h-6 text-blue-600" />
                        </div>
                      </div>
                      <div className="text-3xl font-bold text-blue-600 mb-2">{stat.number}</div>
                      <div className="text-gray-600 font-medium">{stat.label}</div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Achievements Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Achievements</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Recognition and milestones that demonstrate our commitment to excellence in Calgary property management.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {achievements.map((achievement, index) => (
                <motion.div
                  key={achievement}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-gray-50 rounded-lg p-6 flex items-center gap-4"
                >
                  <SafeIcon icon={FiCheck} className="w-6 h-6 text-blue-600 flex-shrink-0" />
                  <span className="text-gray-800 font-medium">{achievement}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact CTA Section */}
        <section className="py-20 bg-blue-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <h2 className="text-4xl font-bold mb-4">Ready to Partner With Calgary's Best?</h2>
              <p className="text-xl text-blue-100 max-w-2xl mx-auto">
                Join hundreds of satisfied property owners who trust DGM Property Management with their valuable investments.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a
                  href="/contact"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
                >
                  Get Started Today
                </motion.a>
                <motion.a
                  href="tel:587-800-1215"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="border-2 border-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
                >
                  Call 587-800-1215
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AboutPage;